/**
 * agentGroups.ts — the Agents list grouped by job, with a one-line sub-heading per agent (SUN-169, mockup C).
 * Order: MANAGER · DEVELOPERS (lead first, then dev) · REVIEWER & TESTER · BROWSER OPERATORS (admin only) · OTHER.
 * Empty sections are hidden. Roles come from the agent link (`agentRole`), never from the name.
 */
import type { KiaanAgent } from './kiaanApi';

export interface AgentGroup {
  key: 'manager' | 'developers' | 'reviewer' | 'browser' | 'other';
  title: string;
  agents: KiaanAgent[];
}

const SECTIONS: Array<{ key: AgentGroup['key']; title: string; roles: string[] }> = [
  { key: 'manager', title: 'MANAGER', roles: ['manager'] },
  { key: 'developers', title: 'DEVELOPERS', roles: ['lead', 'dev'] },
  { key: 'reviewer', title: 'REVIEWER & TESTER', roles: ['reviewer'] },
  { key: 'browser', title: 'BROWSER OPERATORS', roles: ['browser'] },
];

export function groupAgentsByJob(agents: KiaanAgent[], opts: { isAdmin: boolean }): AgentGroup[] {
  const list = agents.filter((a) => !a.main);
  const known = new Set(SECTIONS.flatMap((s) => s.roles));
  const groups: AgentGroup[] = SECTIONS.filter((s) => s.key !== 'browser' || opts.isAdmin).map((s) => ({
    key: s.key,
    title: s.title,
    // Within a section: role order first (lead before dev), then the list order (link order).
    agents: s.roles.flatMap((r) => list.filter((a) => a.agentRole === r)),
  }));
  // Browser operators are admin-only: they are not shown in OTHER either.
  const other = list.filter((a) => !known.has(a.agentRole ?? ''));
  groups.push({ key: 'other', title: 'OTHER', agents: other });
  return groups.filter((g) => g.agents.length > 0);
}

/** "Browser 1", "Browser 2" … in list (link) order. */
function browserNumber(agent: KiaanAgent, all: KiaanAgent[]): number {
  return all.filter((a) => a.agentRole === 'browser').findIndex((a) => a.id === agent.id) + 1;
}

const engineLabel = (a: KiaanAgent): string | null => a.engine?.model ?? null;
const join = (parts: Array<string | null>) => parts.filter(Boolean).join(' · ');

/** The grey line under a name in the list. */
export function agentSubheading(agent: KiaanAgent, all: KiaanAgent[]): string {
  switch (agent.agentRole) {
    case 'manager': return join(['Runs the team', engineLabel(agent)]);
    case 'lead': return join(['Lead', engineLabel(agent)]);
    case 'browser': return join([`Browser ${browserNumber(agent, all)}`, engineLabel(agent)]);
    default: return engineLabel(agent) ?? '';
  }
}

/** The chat header: "Talking to Arjun · Manager · Hermes". */
export function agentHeadline(agent: KiaanAgent, all: KiaanAgent[]): string {
  const job =
    agent.agentRole === 'manager' ? 'Manager'
    : agent.agentRole === 'lead' ? 'Lead'
    : agent.agentRole === 'dev' ? 'Developer'
    : agent.agentRole === 'reviewer' ? 'Reviewer'
    : agent.agentRole === 'browser' ? `Browser ${browserNumber(agent, all)}`
    : null;
  return join([`Talking to ${agent.name}`, job, engineLabel(agent)]);
}
